import React, { useCallback, useEffect, useState } from "react";
import { AlarmClock, Download, Loader2, Send, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { downloadCSV, fdate, money } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { EmptyState, LoadingRows } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Field } from "@/pages/crm/LeadForm";
import { DocumentView } from "./documents/DocumentView";
import { cn } from "@/lib/utils";

const BUCKET_TONE = {
  "Not due": "bg-info-soft text-info border-info/25",
  "1-30 days": "bg-warning-soft text-warning border-warning/30",
  "31-60 days": "bg-warning-soft text-warning border-warning/30",
  "61-90 days": "bg-destructive-soft text-destructive border-destructive/25",
  "90+ days": "bg-destructive-soft text-destructive border-destructive/25",
};

export default function Receivables() {
  const { can } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bucket, setBucket] = useState("all");
  const [viewId, setViewId] = useState(null);
  const [reminder, setReminder] = useState(null);
  const [mail, setMail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/receivables");
      setData(data);
    } catch (e) { toast.error(errMsg(e)); } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openReminder = async (row) => {
    setReminder(row);
    setMail(null);
    try {
      const { data } = await api.get(`/invoices/${row.id}/reminder-preview`);
      setMail(data);
    } catch (e) { toast.error(errMsg(e)); setReminder(null); }
  };

  const sendReminder = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/invoices/${reminder.id}/reminder`, mail);
      if (res.data.status === "sent") toast.success("Payment reminder sent");
      else if (res.data.status === "outbox") toast.warning("SMTP is not configured. The reminder is saved in the Email Outbox.");
      else toast.error(res.data.error);
      setReminder(null);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const rows = (data?.rows || []).filter((r) => bucket === "all" || r.bucket === bucket);

  const columns = [
    { key: "number", label: "Invoice", render: (r) => <span className="num text-sm font-semibold">{r.number}</span> },
    {
      key: "customer", label: "Customer",
      render: (r) => (
        <div className="min-w-0">
          <p className="truncate text-sm font-medium">{r.customer_name}</p>
          <p className="truncate text-xs text-muted-foreground">{r.company}</p>
        </div>
      ),
    },
    { key: "due_date", label: "Due", render: (r) => <span className="text-sm text-muted-foreground">{fdate(r.due_date)}</span>, hideMobile: true },
    {
      key: "overdue_days", label: "Ageing",
      render: (r) => (
        <span className={cn("inline-flex rounded-full border px-2.5 py-0.5 text-xs font-medium", BUCKET_TONE[r.bucket])}>
          {r.overdue_days > 0 ? `${r.overdue_days} days overdue` : "Not due yet"}
        </span>
      ),
    },
    { key: "total", label: "Invoice", align: "right", render: (r) => <span className="num text-sm">{money(r.total)}</span>, hideMobile: true },
    { key: "paid_amount", label: "Received", align: "right", render: (r) => <span className="num text-sm text-success">{money(r.paid_amount)}</span>, hideMobile: true },
    { key: "outstanding", label: "Outstanding", align: "right", render: (r) => <span className="num text-sm font-bold text-destructive">{money(r.outstanding)}</span> },
    {
      key: "actions", label: "", align: "right",
      render: (r) => can("invoices", "email") ? (
        <div onClick={(e) => e.stopPropagation()}>
          <Button size="sm" variant="outline" onClick={() => openReminder(r)} data-testid={`remind-${r.number}`}>
            <Send className="h-3.5 w-3.5" /> Remind
          </Button>
        </div>
      ) : null,
    },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        icon={Wallet}
        title="Receivables"
        subtitle="Money still to come in, grouped by how late it is."
        actions={rows.length > 0 && (
          <Button variant="outline" onClick={() => {
            downloadCSV(rows.map((r) => ({
              Invoice: r.number, Customer: r.customer_name, Company: r.company, Due: r.due_date,
              "Overdue Days": r.overdue_days, Total: r.total, Received: r.paid_amount, Outstanding: r.outstanding,
            })), "receivables.csv");
            toast.success("Receivables exported");
          }}><Download className="h-4 w-4" /> Export</Button>
        )}
      />

      {loading ? <LoadingRows /> : !data?.rows?.length ? (
        <EmptyState icon={Wallet} title="Nothing outstanding"
          description="Every invoice you can see has been fully paid. Nice." />
      ) : (
        <>
          <div className="grid gap-4 sm:grid-cols-3">
            <Card><CardContent className="py-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Total outstanding</p>
              <p className="num mt-1 text-2xl font-bold">{money(data.total_outstanding)}</p>
            </CardContent></Card>
            <Card className="border-destructive/30"><CardContent className="py-5">
              <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Overdue amount</p>
              <p className="num mt-1 text-2xl font-bold text-destructive">{money(data.overdue_outstanding)}</p>
            </CardContent></Card>
            <Card><CardContent className="flex items-center gap-3 py-5">
              <AlarmClock className="h-8 w-8 text-warning" />
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-muted-foreground">Overdue invoices</p>
                <p className="num mt-1 text-2xl font-bold">{data.overdue_count}</p>
              </div>
            </CardContent></Card>
          </div>

          <div className="flex flex-wrap gap-2">
            <button onClick={() => setBucket("all")}
              className={cn("rounded-lg border px-3 py-2 text-sm transition-colors",
                bucket === "all" ? "border-primary bg-primary-soft text-primary" : "border-border hover:border-primary/40")}>
              All ({data.rows.length})
            </button>
            {data.buckets.map((b) => (
              <button key={b.bucket} onClick={() => setBucket(b.bucket)} data-testid={`bucket-${b.bucket}`}
                className={cn("rounded-lg border px-3 py-2 text-left text-sm transition-colors",
                  bucket === b.bucket ? "border-primary bg-primary-soft text-primary" : "border-border hover:border-primary/40")}>
                <span className="block font-medium">{b.bucket}</span>
                <span className="num block text-xs text-muted-foreground">{b.count} . {money(b.amount, true)}</span>
              </button>
            ))}
          </div>

          <DataTable columns={columns} rows={rows} onRowClick={(r) => setViewId(r.id)}
            empty={<EmptyState icon={Wallet} title="Nothing in this bucket" />} />
        </>
      )}

      <DocumentView kind="invoices" docId={viewId} open={!!viewId}
        onOpenChange={(v) => !v && setViewId(null)} onChanged={load} />

      <Dialog open={!!reminder} onOpenChange={(v) => !v && setReminder(null)}>
        <DialogContent className="max-h-[92vh] max-w-2xl overflow-y-auto scroll-slim">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Payment reminder</DialogTitle>
            <DialogDescription>
              {reminder?.number} . {reminder?.customer_name} . outstanding {money(reminder?.outstanding)}
            </DialogDescription>
          </DialogHeader>
          {!mail ? (
            <div className="grid h-32 place-items-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4">
              <Field label="To">
                <Input value={mail.to.join(", ")} onChange={(e) => setMail({ ...mail, to: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
              </Field>
              <Field label="CC">
                <Input value={mail.cc.join(", ")} onChange={(e) => setMail({ ...mail, cc: e.target.value.split(",").map((x) => x.trim()).filter(Boolean) })} />
              </Field>
              <Field label="Subject"><Input value={mail.subject} onChange={(e) => setMail({ ...mail, subject: e.target.value })} /></Field>
              <Field label="Message"><Textarea rows={9} value={mail.body} onChange={(e) => setMail({ ...mail, body: e.target.value })} /></Field>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReminder(null)}>Cancel</Button>
            <Button onClick={sendReminder} disabled={busy || !mail} data-testid="send-reminder">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send reminder
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
